import * as changeCase from 'change-case';
import { AnalyticsError } from '../errors';
import { getAdobeDataLayer, getPageLoadPath, push } from './_shared';
import { NAME_PUSH_EVENTS } from '../constants';
import { TYPE_PUSH_EVENTS } from '../constants/_internal';
import { logger } from '../services';
import { file, validation } from '../utils';
/**
 * Returns a value to help the Analytics team determine the order that a user went through the calculator
 * ---
 * For example, did they fill out their zip code first then their name, did they go bottom to top, etc.
 *
 * @param {string} eventName  See the constant, `NAME_PUSH_EVENTS`
 *
 * @see NAME_PUSH_EVENTS
 */

var getIncrementor = function getIncrementor(eventName) {
  var adobeDataLayer = getAdobeDataLayer();
  var incrementor;
  if (eventName === NAME_PUSH_EVENTS.CALCULATOR_LOAD || typeof adobeDataLayer.getState !== 'function') {
    incrementor = 0;
  } else {
    incrementor = adobeDataLayer.getState('calculator.incrementor') + 1;
  }
  return incrementor;
};
/**
 * Builds out event data for calculator interactions
 *
 * @param {Object} params                           To build out event data for generic page interactions
 * @param {string} params.eventName                 Event name. See the keys in CONSTANTS.NAME_PUSH_EVENTS prefixed with
 *                                                  CALCULATOR_
 * @param {string} params.calculatorName            The name of the calculator, which is actually just a duplicate of
 *                                                  the return value from `getLoadPathName()`.
 *
 *                                                  The reason we still have it is partly for legacy reasons, and partly
 *                                                  for situations where the calculator may be embedded into other
 *                                                  content
 * @param {string} [params.actionType=undefined]    Generally the input field's name/id. Not required for the load event
 * @param {string} [params.actionValues=undefined]  The value in the input field. Not required for the load event
 * @param {Object} [params.applicationData={}]      The application data for the calculator
 *
 * @return void
 *
 * @see NAME_PUSH_EVENTS
 */

var pushEventCalculator = function pushEventCalculator() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    eventName = _ref.eventName,
    calculatorName = _ref.calculatorName,
    _ref$actionType = _ref.actionType,
    actionType = _ref$actionType === void 0 ? undefined : _ref$actionType,
    _ref$actionValues = _ref.actionValues,
    actionValues = _ref$actionValues === void 0 ? undefined : _ref$actionValues,
    _ref$applicationData = _ref.applicationData,
    applicationData = _ref$applicationData === void 0 ? {} : _ref$applicationData;
  if (!eventName || !calculatorName) {
    logger.error(new AnalyticsError("Missing required event name and/or calculator name", TYPE_PUSH_EVENTS.CALCULATOR));
  } // Not entirely necessary as clients can't call this function directly, but this is
  // helpful just for ourselves in case we forget to add a new event

  if (!file.getConstantsByPrefix(NAME_PUSH_EVENTS, 'CALCULATOR_').includes(eventName)) {
    logger.error(new AnalyticsError("The following calculator event is not valid, \"".concat(eventName, ". Check the 'NAME_PUSH_EVENTS' constant to see if it exists there\""), TYPE_PUSH_EVENTS.CALCULATOR));
  }
  var eventData = {
    name: "Calculator: ".concat(getPageLoadPath()),
    incrementor: getIncrementor(eventName)
  };
  switch (eventName) {
    case NAME_PUSH_EVENTS.CALCULATOR_COMPLETE:
    case NAME_PUSH_EVENTS.CALCULATOR_INTERACTION:
      {
        if (!actionType || !actionValues) {
          logger.error(new AnalyticsError("When submitting a \"".concat(eventName, "\" event, you must set the 'actionType' & 'actionValues' params"), TYPE_PUSH_EVENTS.CALCULATOR));
        } // Strip PII  (like 'john' or '342-589-2399'), and normalize to param-case/kebab-case

        var actionTypeSanitized = changeCase.paramCase(actionType);
        var actionValuesSanitized = validation.isFieldNamePii(actionTypeSanitized) ? 'completed' : String(actionValues).substring(0, 255);
        eventData['action-keys'] = calculatorName;
        eventData['action-type'] = actionTypeSanitized;
        eventData['action-values'] = actionValuesSanitized;
        if (eventName === 'calculator-complete') {
          eventData['application-data'] = applicationData;
        }
        break;
      }
  }
  push(eventName, {
    calculator: eventData
  });
};
/**
 * After the user clicks the CTA to begin the calculator. Confusingly, this is NOT when it first loads onto the page
 *
 * @param {Object} params
 * @param {string} params.calculatorName
 *    This value isn't used here, as it's only passed in as the `action-keys` value, which the `load` event does not
 *    have. TODO should it though? This value is useful if/when we have calculators embedded into other content, as
 *    then, the `name` property will be misleading. Remember that the `name` prop is essentially the URL path.
 */

var load = function load(_ref2) {
  var _ref2$calculatorName = _ref2.calculatorName,
    calculatorName = _ref2$calculatorName === void 0 ? undefined : _ref2$calculatorName;
  return pushEventCalculator({
    eventName: NAME_PUSH_EVENTS.CALCULATOR_LOAD,
    calculatorName: calculatorName
  });
};
/**
 * Any interactions with the calculator that changes its state, or that the analytics wants
 *
 * Note that the actual event name's string is "interaction". This function is named "interact" to maintain consistency
 * with all the other event types
 *
 * @param {Object} params
 * @param {string} params.calculatorName
 *    A value like `'retirement-calculator'`. This is eventually set as the `action-keys` prop in the base data layer
 *    call, and is different from the `name` prop which is set automatically
 * @param {string} params.actionType
 *    The name of the input field, such as `'gender'`, `'last_name'`, etc. This value is automatically normalized to be
 *    snake_case, as values across other content is aggregated, and ideally, the field name here is the same across
 *    the entire org
 * @param {string} params.actionValues
 *    The user-provided value for the input field. This is auto-sanitized and changed to `'complete'` for PII fields.
 *    The list of PII fields used is set within this library.
 *
 * @see validation.isFieldNamePii
 */

var interact = function interact(_ref3) {
  var calculatorName = _ref3.calculatorName,
    actionType = _ref3.actionType,
    actionValues = _ref3.actionValues;
  return pushEventCalculator({
    eventName: NAME_PUSH_EVENTS.CALCULATOR_INTERACTION,
    calculatorName: calculatorName,
    actionType: actionType,
    actionValues: actionValues
  });
};
/**
 * To be fired when calculator results are displayed
 *
 * @param {Object} params
 * @param {string} params.calculatorName
 *    A value like 'retirement-calculator'. This is eventually set as the `action-keys` prop in the base data layer
 *    call, and is different from the `name` prop which is set automatically
 * @param {string} params.actionType
 *    Normally, a value like `'calculator-results-retirement'`
 * @param {string} params.actionValues
 *    A concatenated list of calculated values based on user input.
 *    e.g., `'projected_monthly_income: 25680.386540924843 | monthly_savings_change: -15332.212930555248' | ...`
 * @param {Object} params.applicationData
 *    Application data after calculator submission
 */

var complete = function complete(_ref4) {
  var calculatorName = _ref4.calculatorName,
    actionType = _ref4.actionType,
    actionValues = _ref4.actionValues,
    applicationData = _ref4.applicationData;
  return pushEventCalculator({
    eventName: NAME_PUSH_EVENTS.CALCULATOR_COMPLETE,
    calculatorName: calculatorName,
    actionType: actionType,
    actionValues: actionValues,
    applicationData: applicationData
  });
};
export { load, interact, complete };